import { useAuthStore } from "../../store/authStore";
import { useListStore } from "../../store/listStore";
import { useSavedListStore } from "../../store/savedListStore";
import AddIcon from "@mui/icons-material/Add";
import ListIcon from "@mui/icons-material/List";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Input,
  List,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Option,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/joy";
import React from "react";
import { useListSearchSort, SortOption } from "../../hooks/useListSearchSort";

export const PLACES_AUTOCOMPLETE_WIDTH = 300;

type ListSummaryProps = {
  onListClick: (listId: string, isSavedList: boolean) => void;
};

export const ListSummary = ({ onListClick }: ListSummaryProps) => {
  const theme = useTheme();

  const { profile } = useAuthStore();
  const { lists, fetchLists, createList } = useListStore();
  const { savedLists, fetchSavedListsWithDetails } = useSavedListStore();

  const [isCreatingList, setIsCreatingList] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sortOption, setSortOption] = React.useState<SortOption>("date-newest");

  const { filteredOwnLists, filteredSavedLists } = useListSearchSort({
    lists,
    savedLists,
    searchQuery,
    sortOption,
  });

  React.useEffect(() => {
    if (!profile?.id) {
      return;
    }
    fetchLists(profile.id);
    fetchSavedListsWithDetails(profile.id);
  }, [profile?.id, fetchLists, fetchSavedListsWithDetails]);

  const onNewListClick = () => {
    if (!profile?.id) {
      return;
    }
    setIsCreatingList(true);
    createList({
      title: "Untitled List",
      user_id: profile.id,
      privacy_level: "public",
    }).then(({ id }) => {
      onListClick(id, false);
      setIsCreatingList(false);
    });
  };

  const hasNoResults =
    filteredOwnLists.length === 0 && filteredSavedLists.length === 0;

  return (
    <Box height="inherit" display="flex" flexDirection="column">
      <Box paddingLeft={theme.spacing(2)} paddingRight={theme.spacing(2)}>
        <Button
          onClick={onNewListClick}
          loading={isCreatingList}
          variant="soft"
          startDecorator={<AddIcon />}
          fullWidth
          data-testid="new-list-button"
        >
          New List
        </Button>
      </Box>
      <Box px={2} pt={1.5}>
        <Stack spacing={1}>
          <Input
            placeholder="Search lists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            startDecorator={<SearchIcon />}
            endDecorator={
              searchQuery ? (
                <IconButton
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                  data-testid="list-search-clear"
                >
                  <CloseIcon />
                </IconButton>
              ) : null
            }
            data-testid="list-search-input"
          />
          <Select
            value={sortOption}
            onChange={(_e, val) => {
              if (val) setSortOption(val as SortOption);
            }}
            startDecorator={<SortIcon />}
            data-testid="list-sort-select"
          >
            <Option value="date-newest">Newest first</Option>
            <Option value="date-oldest">Oldest first</Option>
            <Option value="title-asc">Title A-Z</Option>
            <Option value="title-desc">Title Z-A</Option>
          </Select>
        </Stack>
      </Box>
      <Box
        flexGrow={1}
        sx={{
          overflowY: "scroll",
        }}
      >
        {searchQuery && hasNoResults ? (
          <Box px={2} pt={2}>
            <Typography
              level="body-sm"
              sx={{ color: "text.secondary" }}
              data-testid="list-search-empty"
            >
              No lists match &ldquo;{searchQuery}&rdquo;
            </Typography>
          </Box>
        ) : (
          <>
            <List component="nav">
              {filteredOwnLists.map((list) => (
                <ListItemButton
                  key={list.id}
                  onClick={() => onListClick(list.id.toString(), false)}
                  data-testid={`list-item-${list.id}`}
                >
                  <ListItemDecorator>
                    <ListIcon />
                  </ListItemDecorator>
                  <ListItemContent>
                    <Typography>{list.title || "Untitled List"}</Typography>
                  </ListItemContent>
                </ListItemButton>
              ))}
            </List>
            {filteredSavedLists.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  level="title-sm"
                  sx={{ px: theme.spacing(2), mb: 1 }}
                >
                  Saved Lists
                </Typography>
                <List component="nav">
                  {filteredSavedLists.map((saved) => (
                    <ListItemButton
                      key={saved.list_id}
                      onClick={() => onListClick(saved.list_id, true)}
                      data-testid={`saved-list-item-${saved.list_id}`}
                    >
                      <ListItemDecorator>
                        <BookmarkIcon />
                      </ListItemDecorator>
                      <ListItemContent>
                        <Typography>{saved.list.title}</Typography>
                        <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                          by @{saved.list.profiles?.username}
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
